/**
 * Шкала растрового слоя: цвета, единица и края диапазона.
 *
 * ОДИН источник на экран и на файл. Замечание пользователей 07.09.2026:
 * «легенда на картинках при скачивании не всегда содержит описание к картинке».
 * Разбор показал две дыры, и обе закрываются отсюда:
 *
 *  * плашка скачанного кадра называла слой СЛОВАМИ («Комфортная среда, 20 м») и
 *    не несла ни цветовой шкалы, ни единицы, ни краёв — то есть по картинке
 *    нельзя было сказать, что означает цвет;
 *  * в разделе «Аналитика» шкалы не было и НА ЭКРАНЕ: компонент `maps/Legend`
 *    рисовали только «Карты», а из трёх моделей своя легенда была лишь у
 *    апвеллинга.
 *
 * Здесь чистая часть; рисование — у компонента и у `exportImage`.
 */
import type { LayerPalette } from '../api'
import { t, unitLabel } from '../i18n'
import { layerLabel } from '../i18n/apiLabels'
import { paletteGradient } from './colormaps'
import { formatLegendValue } from './legendFormat'
import type { PlateMark } from './legendRows'

/** Слои, у которых шкала НЕ числовая, — каждый со своей причиной. */
const WORD_TICKS: Record<string, readonly string[]> = {
  // Туман: три состояния, промежуточного не бывает — градиент здесь врал бы.
  sea_fog: ['maps.legend.fog.none', 'maps.legend.fog.haze', 'maps.legend.fog.fog'],
  // Замороженный индекс отрыва безразмерен, и это ВИДИМАЯ разница: слой,
  // который океанолог назвал неверным, не притворяется измерением.
  upwelling: ['models.upwelling.low', 'models.upwelling.medium', 'models.upwelling.high'],
}

export interface ScaleInput {
  layerId: string
  /** Код единицы из каталога сервера; пустой — величины нет. */
  unit: string
  palette: LayerPalette | undefined
  /** Края, посчитанные по отрисованным тайлам.
   *
   *  ТРИ ответа, и два из них путать нельзя. Число — края известны. `null` —
   *  СПРАШИВАЛИ и не знаем (тайлы ещё не отрисованы), и тогда шкала честно
   *  подписывается словами. `undefined` — НЕ СПРАШИВАЛИ: у слоя своя палитра с
   *  готовыми опорами (механизмы апвеллинга), и края берутся из неё. Свести
   *  последние два значило бы либо потерять готовую шкалу, либо выдать опоры
   *  палитры за измеренный диапазон. */
  min?: number | null
  max?: number | null
}

export interface ScaleView {
  label: string
  /** Переведённая единица; пустая строка — величины нет либо шкала безразмерна. */
  unit: string
  /** Цвета опор как CSS — ими рисуется образец в плашке кадра (холст умеет
   *  только линейный градиент по опорам). */
  stops: string[]
  /** Готовая заливка образца для экрана. У дискретной шкалы (туман) это ПОЛОСЫ,
   *  а не градиент: промежуточного состояния у неё не бывает, и плавный переход
   *  обещал бы «полутуман». */
  gradient: string
  /** Подписи под шкалой: два края числами либо слова состояний. */
  ticks: string[]
  /** Подписаны ли края ЧИСЛАМИ. Ложь — тайлы ещё не готовы либо шкала словесная. */
  numeric: boolean
}

const css = (c: readonly number[]): string => `rgb(${c[0]},${c[1]},${c[2]})`

/** Шкала слоя либо `null`, если красить его шкалой нечем (виджет, геометрия). */
export function scaleView(input: ScaleInput): ScaleView | null {
  const { layerId, palette } = input
  if (!palette || palette.colors.length < 2) return null
  const stops = palette.colors.map(css)
  const words = WORD_TICKS[layerId]
  if (words) {
    return {
      label: layerLabel(layerId),
      unit: '',
      stops,
      gradient: paletteGradient(palette),
      ticks: words.map(k => t(k)),
      numeric: false,
    }
  }
  const asked = 'min' in input || 'max' in input
  const lo = asked ? input.min : palette.stops[0]
  const hi = asked ? input.max : palette.stops[palette.stops.length - 1]
  const numeric =
    lo != null && hi != null && Number.isFinite(lo) && Number.isFinite(hi) && hi > lo
  const ticks = numeric
    ? [formatLegendValue(lo, hi - lo), formatLegendValue(hi, hi - lo)]
    // Числа неизвестны — говорим словами. Ноль на месте неизвестного края
    // назвал бы измерение, которого никто не делал.
    : [t('maps.legend.min'), t('maps.legend.max')]
  return {
    label: layerLabel(layerId),
    unit: unitLabel(input.unit),
    stops,
    gradient: paletteGradient(palette),
    ticks,
    numeric,
  }
}

/**
 * Строка шкалы для плашки скачанного кадра.
 *
 * Слой на плашке уже назван заголовком, поэтому здесь только величина: единица
 * и края. Образец — ГРАДИЕНТ, а не один цвет: одним цветом шкалу не показать,
 * а именно она и объясняет картинку.
 */
export function scalePlateLine(v: ScaleView): { text: string; mark: PlateMark } {
  const range = v.ticks.length > 2 ? v.ticks.join(' · ') : v.ticks.join('…')
  return {
    text: [v.unit, range].filter(Boolean).join(' '),
    mark: { kind: 'ramp', stops: v.stops },
  }
}
